import { useEffect, useMemo, useState } from 'react';
import { api, fetchCompetentPersonOptions } from '../lib/api.js';
import { Modal } from './Modal.js';
import { InlinePersonnelForm, Personnel } from './InlinePersonnelForm.js';
import { InlineTestMethodForm } from './InlineTestMethodForm.js';
import { generateForm10ClientPDF } from '../lib/pdf-form10-client.js';
import {
  DEFAULT_TEST_METHOD,
  buildTestMethodOptions,
  getEquipmentRule,
  buildPoint5Sentence,
} from '@gng-ehs/shared/equipment-rules';

export interface RenewalItem {
  id: string;
  certificate_number: string;
  company_name: string;
  equipment_name: string;
  internal_id_number: string;
  examination_date: string;
  next_inspection_date: string;
  test_method?: string;
  status: 'COMPLIANT' | 'EXPIRING_SOON' | 'OVERDUE';
  days_until_due: number;
  type?: 'FORM_9' | 'FORM_10' | 'FORM_11' | 'TRAINING';
}

export interface StagedForm10Renewal {
  id: string; // old certificate ID
  certificate_number: string;
  company_name: string;
  equipment_name: string;
  internal_id_number: string;
  last_examination_date: string;
  next_due_date: string;
  status: string;

  // Editable renewal values
  examination_date: string;
  personnel_id: string;
  personnel_name?: string;
  test_method: string;
  defect_status: 'SAFE' | 'REJECT' | 'NOT_APPLICABLE';
  defect_remarks: string;

  // Cached full cert data for preview
  fullCertData?: Record<string, unknown>;
  loadingCert?: boolean;
}

export interface Form10StagedGroup {
  key: string;
  company_name: string;
  last_examination_date: string;
  items: StagedForm10Renewal[];
}

interface Form10BulkRenewModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  selectedItems?: RenewalItem[];
  allItems: RenewalItem[];
}

function nextInspDate10(examISO: string): string {
  if (!examISO) return '';
  const d = new Date(examISO + 'T00:00:00');
  d.setFullYear(d.getFullYear() + 1);
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
}

function toDDMMYYYY(iso: string): string {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    return `${d?.padStart(2, '0')}-${m?.padStart(2, '0')}-${y}`;
  } catch {
    return iso;
  }
}

export function Form10BulkRenewModal({
  open,
  onClose,
  onSuccess,
  selectedItems,
  allItems,
}: Form10BulkRenewModalProps) {
  const [stagedRows, setStagedRows] = useState<StagedForm10Renewal[]>([]);
  const [companyFilter, setCompanyFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('OVERDUE');

  // Batch toolbar state
  const [batchDate, setBatchDate] = useState(new Date().toISOString().slice(0, 10));
  const [batchPersonnelId, setBatchPersonnelId] = useState('');
  const [batchTestMethod, setBatchTestMethod] = useState(DEFAULT_TEST_METHOD);
  const [batchDefectStatus, setBatchDefectStatus] = useState<'SAFE' | 'REJECT' | 'NOT_APPLICABLE'>('SAFE');

  // Inspector options list for inline dropdowns
  const [inspectors, setInspectors] = useState<Array<{ id: string; full_name: string }>>([]);
  const [customTestMethods, setCustomTestMethods] = useState<Array<{ id: string; name: string }>>([]);

  // Modals for creating new inspector/method inline
  const [showNewPersonnelModal, setShowNewPersonnelModal] = useState(false);
  const [showNewMethodModal, setShowNewMethodModal] = useState(false);

  // Live PDF preview state
  const [previewRow, setPreviewRow] = useState<StagedForm10Renewal | null>(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitProgress, setSubmitProgress] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Load inspectors and test methods
  useEffect(() => {
    if (!open) return;
    fetchCompetentPersonOptions('')
      .then((opts) => setInspectors(opts.map((o) => ({ id: o.value, full_name: o.label }))))
      .catch(() => {});
    api.testMethods.list()
      .then((res) => { if (res.data) setCustomTestMethods(res.data); })
      .catch(() => {});
  }, [open]);

  const allTestMethodOptions = useMemo(() => {
    return buildTestMethodOptions(customTestMethods);
  }, [customTestMethods]);

  // Initialize staged rows when modal opens
  useEffect(() => {
    if (!open) return;
    const sourceItems = (selectedItems && selectedItems.length > 0)
      ? selectedItems.filter((i) => i.type !== 'FORM_11')
      : allItems.filter((i) => i.type !== 'FORM_11');

    const todayISO = new Date().toISOString().slice(0, 10);
    const initialRows: StagedForm10Renewal[] = sourceItems.map((item) => ({
      id: item.id,
      certificate_number: item.certificate_number,
      company_name: item.company_name,
      equipment_name: item.equipment_name,
      internal_id_number: item.internal_id_number,
      last_examination_date: item.examination_date,
      next_due_date: item.next_inspection_date,
      status: item.status,
      examination_date: todayISO,
      personnel_id: '',
      test_method: item.test_method || DEFAULT_TEST_METHOD,
      defect_status: 'SAFE',
      defect_remarks: '',
    }));

    const hasOverdue = initialRows.some((r) => r.status === 'OVERDUE');
    const defaultStatus = (selectedItems && selectedItems.length > 0)
      ? 'ALL'
      : hasOverdue
        ? 'OVERDUE'
        : 'ALL';

    setStagedRows(initialRows);
    setErrorMessage('');
    setCompanyFilter('ALL');
    setStatusFilter(defaultStatus);
    setPreviewRow(null);
    setPreviewPdfUrl(null);
  }, [open, selectedItems, allItems]);

  // Unique companies among staged rows for filter
  const companyOptions = useMemo(() => {
    const names = Array.from(new Set(stagedRows.map((r) => r.company_name))).filter(Boolean);
    return names.sort();
  }, [stagedRows]);

  // Filtered rows for display
  const displayedRows = useMemo(() => {
    return stagedRows.filter((r) => {
      if (companyFilter !== 'ALL' && r.company_name !== companyFilter) return false;
      if (statusFilter !== 'ALL' && r.status !== statusFilter) return false;
      return true;
    });
  }, [stagedRows, companyFilter, statusFilter]);

  // Batch apply helpers
  const handleApplyBatchDate = () => {
    if (!batchDate) return;
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? { ...row, examination_date: batchDate }
          : row
      )
    );
  };

  const handleApplyBatchInspector = () => {
    if (!batchPersonnelId) return;
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? { ...row, personnel_id: batchPersonnelId }
          : row
      )
    );
  };

  const handleApplyBatchTestMethod = () => {
    if (!batchTestMethod) return;
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? { ...row, test_method: batchTestMethod }
          : row
      )
    );
  };

  const handleApplyBatchDefectStatus = () => {
    setStagedRows((prev) =>
      prev.map((row) =>
        displayedRows.some((d) => d.id === row.id)
          ? {
              ...row,
              defect_status: batchDefectStatus,
              defect_remarks: (batchDefectStatus === 'SAFE' || batchDefectStatus === 'NOT_APPLICABLE') ? '' : row.defect_remarks,
            }
          : row
      )
    );
  };

  const updateRow = (id: string, partial: Partial<StagedForm10Renewal>) => {
    setStagedRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ...partial } : r))
    );
  };

  const removeRow = (id: string) => {
    setStagedRows((prev) => prev.filter((r) => r.id !== id));
  };

  // Live PDF preview for a specific row
  const openRowPreview = async (row: StagedForm10Renewal) => {
    setPreviewRow(row);
    setPreviewPdfUrl(null);

    let cert = row.fullCertData;
    if (!cert) {
      try {
        const res = await api.form10.get(row.id);
        cert = ((res as any).data ?? res) as Record<string, unknown>;
        updateRow(row.id, { fullCertData: cert });
      } catch {
        cert = {};
      }
    }

    try {
      const examISO = row.examination_date || new Date().toISOString().slice(0, 10);
      const nextInsp = nextInspDate10(examISO);
      const examDateDD = toDDMMYYYY(examISO);

      const equipName = (cert.equipment_short_code as string) || (cert.equipment_name_type as string) || '';
      const rule = equipName ? getEquipmentRule(equipName) : undefined;
      const swl = (cert.swl_value as number) ?? null;
      const inspector = inspectors.find((i) => i.id === row.personnel_id)?.full_name || (cert.inspector_name as string) || 'Competent Person';
      const point5 = buildPoint5Sentence(rule ?? undefined, swl, examDateDD, inspector, row.test_method || DEFAULT_TEST_METHOD);

      const pdfUrl = generateForm10ClientPDF({
        certificate_number: `${row.certificate_number} (Renewed)`,
        examination_date: examISO,
        company: {
          license_number: cert.license_number as string,
          registration_number: cert.registration_number as string,
          nic_code: cert.nic_code as string,
          occupier_name: cert.occupier_name as string,
          company_name: cert.company_name as string,
          factory_address: cert.factory_address as string,
        },
        equipment: {
          equipment_short_code: cert.equipment_short_code as string,
          internal_id_number: cert.internal_id_number as string,
          swl_value: cert.swl_value as number,
          swl_text: cert.swl_text as string,
          lift_height: cert.lift_height as string,
          make: cert.make as string,
          model: cert.model as string,
          equipment_size: cert.equipment_size as string,
          location_in_factory: cert.location_in_factory as string,
          first_use_value: cert.first_use_value as string,
        },
        test_prefix_sentence: point5,
        previous_inspection_details: `Last Inspection done on ${toDDMMYYYY(cert.examination_date as string)} by ${(cert.inspector_name as string) || 'Competent Person'}`,
        annealing_details: cert.annealing_details as string,
        defect_status: row.defect_status,
        defect_remarks: (row.defect_status === 'SAFE' || row.defect_status === 'NOT_APPLICABLE') ? undefined : row.defect_remarks,
        next_inspection_date: nextInsp,
      });

      setPreviewPdfUrl(pdfUrl);
    } catch {
      setPreviewPdfUrl(null);
    }
  };

  const stagedGroups = useMemo<Form10StagedGroup[]>(() => {
    const map = new Map<string, Form10StagedGroup>();
    for (const row of displayedRows) {
      const key = `${row.company_name}::${row.last_examination_date || 'Unknown'}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          company_name: row.company_name,
          last_examination_date: row.last_examination_date || 'Unknown',
          items: [],
        });
      }
      map.get(key)!.items.push(row);
    }
    return Array.from(map.values());
  }, [displayedRows]);

  const handleApplyGroupValues = (
    groupKey: string,
    values: {
      examination_date?: string;
      personnel_id?: string;
      test_method?: string;
      defect_status?: 'SAFE' | 'REJECT' | 'NOT_APPLICABLE';
    }
  ) => {
    setStagedRows((prev) =>
      prev.map((row) => {
        const rowKey = `${row.company_name}::${row.last_examination_date || 'Unknown'}`;
        if (rowKey === groupKey) {
          return {
            ...row,
            ...(values.examination_date ? { examination_date: values.examination_date } : {}),
            ...(values.personnel_id ? { personnel_id: values.personnel_id } : {}),
            ...(values.test_method ? { test_method: values.test_method } : {}),
            ...(values.defect_status
              ? {
                  defect_status: values.defect_status,
                  defect_remarks:
                    values.defect_status === 'SAFE' || values.defect_status === 'NOT_APPLICABLE'
                      ? ''
                      : row.defect_remarks,
                }
              : {}),
          };
        }
        return row;
      })
    );
  };

  // Validation counts
  const validRows = useMemo(
    () => displayedRows.filter((r) => Boolean(r.personnel_id) && Boolean(r.examination_date)),
    [displayedRows]
  );
  const isValidAll = displayedRows.length > 0 && validRows.length === displayedRows.length;

  // Submit bulk renewal
  const handleBulkSubmit = async () => {
    if (!isValidAll) {
      setErrorMessage('Please assign a Competent Person to all certificates before renewing.');
      return;
    }
    setSubmitting(true);
    setErrorMessage('');
    setSubmitProgress(`Renewing ${displayedRows.length} certificates...`);

    try {
      const payloadItems = displayedRows.map((r) => ({
        old_certificate_id: r.id,
        examination_date: r.examination_date,
        personnel_id: r.personnel_id,
        test_method: r.test_method,
        defect_status: r.defect_status,
        defect_remarks: (r.defect_status === 'SAFE' || r.defect_status === 'NOT_APPLICABLE') ? '' : r.defect_remarks,
      }));

      await api.dashboard.bulkRenew({
        type: 'FORM_10',
        items: payloadItems,
      });

      onSuccess();
      onClose();
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Bulk renewal failed');
    } finally {
      setSubmitting(false);
      setSubmitProgress('');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="⚡ Bulk Renew Form 10 Certificates (Lifting Machines & Tackles)" size="full">
      <div className="flex flex-col h-[calc(100vh-65px)] overflow-hidden">
        {/* Top Info & Filters */}
        <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm shadow-sm">
              🏗️
            </span>
            <div>
              <span className="font-bold text-slate-900 text-sm">
                Staging <span className="text-blue-700">{displayedRows.length}</span> Form 10 Certificates across{' '}
                <span className="text-blue-700">{stagedGroups.length}</span> Inspection Group(s)
              </span>
              <p className="text-xs text-slate-500">
                Organized by company inspection batches. Apply parameters per group or adjust individually.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Filter by company */}
            <select
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 shadow-sm"
            >
              <option value="ALL">All Companies ({companyOptions.length})</option>
              {companyOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>

            {/* Filter by status */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-2.5 py-1.5 bg-white border border-slate-300 rounded-lg text-xs font-medium text-slate-700 shadow-sm"
            >
              <option value="ALL">All Statuses</option>
              <option value="OVERDUE">Overdue Only</option>
              <option value="EXPIRING_SOON">Expiring Soon</option>
              <option value="COMPLIANT">Compliant</option>
            </select>
          </div>
        </div>

        {/* Global Batch Apply All Toolbar */}
        <div className="p-3 bg-blue-50/60 border-b border-blue-200/80 shrink-0 shadow-sm">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="font-bold text-blue-900 shrink-0 flex items-center gap-1.5">
              <span className="text-amber-500 text-sm">⚡</span>
              <span>Global Batch Apply (All Rows):</span>
            </span>

            {/* Batch Date */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-blue-200">
              <span className="text-slate-500 pl-1 font-medium">Date:</span>
              <input
                type="date"
                value={batchDate}
                onChange={(e) => setBatchDate(e.target.value)}
                className="px-2 py-1 text-xs border border-slate-200 rounded font-medium"
              />
              <button
                type="button"
                onClick={handleApplyBatchDate}
                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-xs transition-colors cursor-pointer"
              >
                Apply All
              </button>
            </div>

            {/* Batch Inspector */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-blue-200 min-w-[260px]">
              <span className="text-slate-500 pl-1 font-medium">Inspector:</span>
              <select
                value={batchPersonnelId}
                onChange={(e) => setBatchPersonnelId(e.target.value)}
                className="flex-1 px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-white"
              >
                <option value="">-- Choose Inspector --</option>
                {inspectors.map((ins) => (
                  <option key={ins.id} value={ins.id}>{ins.full_name}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleApplyBatchInspector}
                disabled={!batchPersonnelId}
                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white font-semibold rounded text-xs transition-colors whitespace-nowrap cursor-pointer"
              >
                Apply All
              </button>
              <button
                type="button"
                onClick={() => setShowNewPersonnelModal(true)}
                title="Add New Personnel"
                className="px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded text-xs transition-colors cursor-pointer"
              >
                +
              </button>
            </div>

            {/* Batch Test Method */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-blue-200">
              <span className="text-slate-500 pl-1 font-medium">Test Method:</span>
              <select
                value={batchTestMethod}
                onChange={(e) => setBatchTestMethod(e.target.value)}
                className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-white max-w-[200px]"
              >
                {allTestMethodOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={handleApplyBatchTestMethod}
                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-xs transition-colors cursor-pointer"
              >
                Apply All
              </button>
            </div>

            {/* Batch Defect Status */}
            <div className="flex items-center gap-1.5 bg-white p-1 rounded-lg border border-blue-200">
              <span className="text-slate-500 pl-1 font-medium">Defect Status:</span>
              <select
                value={batchDefectStatus}
                onChange={(e) => setBatchDefectStatus(e.target.value as any)}
                className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-white"
              >
                <option value="SAFE">Safe</option>
                <option value="REJECT">Defective</option>
                <option value="NOT_APPLICABLE">N/A</option>
              </select>
              <button
                type="button"
                onClick={handleApplyBatchDefectStatus}
                className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-xs transition-colors cursor-pointer"
              >
                Apply All
              </button>
            </div>
          </div>
        </div>

        {errorMessage && (
          <div className="m-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs font-medium text-red-700 flex items-center justify-between">
            <span>{errorMessage}</span>
            <button type="button" onClick={() => setErrorMessage('')} className="text-red-500 font-bold">✕</button>
          </div>
        )}

        {/* Grouped Interactive Staging Area */}
        <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-4 bg-slate-50/50">
          {stagedGroups.length === 0 ? (
            <div className="card p-12 text-center text-slate-500 font-medium">
              No certificates staged for renewal.
            </div>
          ) : (
            stagedGroups.map((group) => (
              <Form10GroupCard
                key={group.key}
                group={group}
                inspectors={inspectors}
                allTestMethodOptions={allTestMethodOptions}
                onApplyGroup={handleApplyGroupValues}
                onUpdateRow={updateRow}
                onRemoveRow={removeRow}
                onOpenRowPreview={openRowPreview}
                onOpenNewPersonnel={() => setShowNewPersonnelModal(true)}
              />
            ))
          )}
        </div>

        {/* Bottom Actions Bar */}
        <div className="p-4 bg-slate-50 border-t border-slate-200 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-700">
              Ready: <strong className="text-emerald-700">{validRows.length}</strong> / {displayedRows.length} certificates
            </span>
            {!isValidAll && (
              <span className="text-xs font-medium text-amber-700 bg-amber-100 px-2 py-0.5 rounded">
                ⚠️ {displayedRows.length - validRows.length} row(s) missing Competent Person
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary text-sm h-10 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleBulkSubmit}
              disabled={submitting || !isValidAll}
              className="btn btn-primary text-sm h-10 px-6 font-semibold inline-flex items-center gap-2 shadow-sm disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{submitProgress || 'Renewing…'}</span>
                </>
              ) : (
                <>
                  <span>⚡</span>
                  <span>Renew All ({displayedRows.length} Form 10 Certificates)</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Row Live PDF Preview Modal */}
      {previewRow && (
        <Modal
          open={Boolean(previewRow)}
          onClose={() => setPreviewRow(null)}
          title={`Preview Renewed Form 10: ${previewRow.certificate_number} — ${previewRow.equipment_name}`}
          size="lg"
        >
          <div className="h-[75vh] flex flex-col">
            {previewPdfUrl ? (
              <iframe
                src={previewPdfUrl}
                className="w-full flex-1 rounded-lg border border-slate-300 shadow bg-white"
                title="Renewed Form 10 PDF Preview"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-slate-500 flex-col gap-2">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
                Loading certificate document preview…
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Inline New Personnel Modal */}
      {showNewPersonnelModal && (
        <Modal
          open={showNewPersonnelModal}
          onClose={() => setShowNewPersonnelModal(false)}
          title="Add New Competent Person"
          size="xl"
        >
          <InlinePersonnelForm
            initialRole="competent_person"
            onCreated={(pers: Personnel) => {
              setInspectors((prev) => [...prev, { id: pers.id, full_name: pers.full_name }]);
              setBatchPersonnelId(pers.id);
              setShowNewPersonnelModal(false);
            }}
            onCancel={() => setShowNewPersonnelModal(false)}
          />
        </Modal>
      )}

      {/* Inline New Test Method Modal */}
      {showNewMethodModal && (
        <Modal
          open={showNewMethodModal}
          onClose={() => setShowNewMethodModal(false)}
          title="Add New Test Method"
          size="lg"
        >
          <InlineTestMethodForm
            onCreated={(m) => {
              setCustomTestMethods((prev) => [...prev, m]);
              setBatchTestMethod(m.name);
              setShowNewMethodModal(false);
            }}
            onCancel={() => setShowNewMethodModal(false)}
          />
        </Modal>
      )}
    </Modal>
  );
}

interface Form10GroupCardProps {
  group: Form10StagedGroup;
  inspectors: Array<{ id: string; full_name: string }>;
  allTestMethodOptions: Array<{ value: string; label: string }>;
  onApplyGroup: (
    groupKey: string,
    values: {
      examination_date?: string;
      personnel_id?: string;
      test_method?: string;
      defect_status?: 'SAFE' | 'REJECT' | 'NOT_APPLICABLE';
    }
  ) => void;
  onUpdateRow: (id: string, partial: Partial<StagedForm10Renewal>) => void;
  onRemoveRow: (id: string) => void;
  onOpenRowPreview: (row: StagedForm10Renewal) => void;
  onOpenNewPersonnel: () => void;
}

function Form10GroupCard({
  group,
  inspectors,
  allTestMethodOptions,
  onApplyGroup,
  onUpdateRow,
  onRemoveRow,
  onOpenRowPreview,
  onOpenNewPersonnel,
}: Form10GroupCardProps) {
  const [groupDate, setGroupDate] = useState(new Date().toISOString().slice(0, 10));
  const [groupPersonnelId, setGroupPersonnelId] = useState('');
  const [groupTestMethod, setGroupTestMethod] = useState(DEFAULT_TEST_METHOD);
  const [groupDefectStatus, setGroupDefectStatus] = useState<'SAFE' | 'REJECT' | 'NOT_APPLICABLE'>('SAFE');

  const handleApply = () => {
    onApplyGroup(group.key, {
      examination_date: groupDate,
      personnel_id: groupPersonnelId || undefined,
      test_method: groupTestMethod || undefined,
      defect_status: groupDefectStatus,
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden transition-all">
      {/* Group Header */}
      <div className="p-3 bg-slate-50 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 font-bold flex items-center justify-center text-sm shadow-xs shrink-0">
            🏢
          </span>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-slate-900 text-sm">{group.company_name}</span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                📅 Inspected: {toDDMMYYYY(group.last_examination_date)}
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200">
                {group.items.length} item{group.items.length === 1 ? '' : 's'}
              </span>
            </div>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Inspection batch from {toDDMMYYYY(group.last_examination_date)}. Mass update below or edit individual items.
            </p>
          </div>
        </div>

        {/* Group Mass Apply Bar */}
        <div className="flex flex-wrap items-center gap-2 text-xs bg-white p-1.5 rounded-lg border border-slate-200 shadow-xs">
          <span className="font-semibold text-slate-700 pl-1">⚡ Group Apply:</span>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Date:</span>
            <input
              type="date"
              value={groupDate}
              onChange={(e) => setGroupDate(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50"
              title="Group New Examination Date"
            />
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Inspector:</span>
            <select
              value={groupPersonnelId}
              onChange={(e) => setGroupPersonnelId(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50 max-w-[170px]"
            >
              <option value="">-- Choose Inspector --</option>
              {inspectors.map((ins) => (
                <option key={ins.id} value={ins.id}>{ins.full_name}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={onOpenNewPersonnel}
              title="Add New Personnel"
              className="px-1.5 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded text-xs cursor-pointer"
            >
              +
            </button>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Method:</span>
            <select
              value={groupTestMethod}
              onChange={(e) => setGroupTestMethod(e.target.value)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50 max-w-[150px]"
            >
              {allTestMethodOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500">Status:</span>
            <select
              value={groupDefectStatus}
              onChange={(e) => setGroupDefectStatus(e.target.value as any)}
              className="px-2 py-1 text-xs border border-slate-200 rounded font-medium bg-slate-50"
            >
              <option value="SAFE">Safe</option>
              <option value="REJECT">Defective</option>
              <option value="NOT_APPLICABLE">N/A</option>
            </select>
          </div>

          <button
            type="button"
            onClick={handleApply}
            className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded text-xs shadow-xs transition-colors cursor-pointer whitespace-nowrap"
          >
            Apply to Group
          </button>
        </div>
      </div>

      {/* Items Table in Group */}
      <div className="overflow-x-auto">
        <table className="w-full text-left text-xs border-collapse">
          <thead className="bg-slate-100/70 border-b border-slate-200 text-slate-600 font-semibold uppercase tracking-wider text-[11px]">
            <tr>
              <th className="py-2 px-3 w-10 text-center">#</th>
              <th className="py-2 px-3 min-w-[150px]">REPORT / CERT NO.</th>
              <th className="py-2 px-3 min-w-[180px]">Equipment / ID</th>
              <th className="py-2 px-3 min-w-[130px]">New Exam Date *</th>
              <th className="py-2 px-3 min-w-[100px]">Next Due</th>
              <th className="py-2 px-3 min-w-[180px]">Competent Person *</th>
              <th className="py-2 px-3 min-w-[150px]">Test Method</th>
              <th className="py-2 px-3 min-w-[140px]">Defect Status</th>
              <th className="py-2 px-2 w-14 text-center">Preview</th>
              <th className="py-2 px-2 w-10 text-center">Status</th>
              <th className="py-2 px-2 w-10 text-center">✕</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {group.items.map((row, idx) => {
              const isReady = Boolean(row.personnel_id && row.examination_date);
              return (
                <tr key={row.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2 px-3 text-center text-slate-400 font-mono text-[11px]">{idx + 1}</td>
                  <td className="py-2 px-3">
                    <span className="font-mono font-semibold text-slate-900 block">{row.certificate_number}</span>
                    <span className="text-[10px] text-slate-400">Due: {toDDMMYYYY(row.next_due_date)}</span>
                  </td>
                  <td className="py-2 px-3">
                    <span className="font-medium text-slate-800 block truncate max-w-[200px]" title={row.equipment_name}>
                      {row.equipment_name}
                    </span>
                    {row.internal_id_number && (
                      <span className="text-[10px] font-mono text-slate-500 bg-slate-100 px-1 py-0.5 rounded">
                        ID: {row.internal_id_number}
                      </span>
                    )}
                  </td>
                  <td className="py-2 px-3">
                    <input
                      type="date"
                      value={row.examination_date}
                      onChange={(e) => onUpdateRow(row.id, { examination_date: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white focus:border-blue-500"
                    />
                  </td>
                  <td className="py-2 px-3 text-slate-600 font-mono text-[11px] whitespace-nowrap">
                    {toDDMMYYYY(nextInspDate10(row.examination_date))}
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.personnel_id}
                      onChange={(e) => onUpdateRow(row.id, { personnel_id: e.target.value })}
                      className={`w-full px-2 py-1 text-xs border rounded font-medium bg-white ${
                        !row.personnel_id ? 'border-amber-400 bg-amber-50/30' : 'border-slate-300'
                      }`}
                    >
                      <option value="">-- Select Competent Person --</option>
                      {inspectors.map((ins) => (
                        <option key={ins.id} value={ins.id}>{ins.full_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.test_method}
                      onChange={(e) => onUpdateRow(row.id, { test_method: e.target.value })}
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      {allTestMethodOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-2 px-3">
                    <select
                      value={row.defect_status}
                      onChange={(e) =>
                        onUpdateRow(row.id, {
                          defect_status: e.target.value as any,
                          defect_remarks:
                            e.target.value === 'SAFE' || e.target.value === 'NOT_APPLICABLE'
                              ? ''
                              : row.defect_remarks,
                        })
                      }
                      className="w-full px-2 py-1 text-xs border border-slate-300 rounded font-medium bg-white"
                    >
                      <option value="SAFE">Safe</option>
                      <option value="REJECT">Defective</option>
                      <option value="NOT_APPLICABLE">N/A</option>
                    </select>
                    {row.defect_status === 'REJECT' && (
                      <input
                        type="text"
                        placeholder="Defect remarks..."
                        value={row.defect_remarks}
                        onChange={(e) => onUpdateRow(row.id, { defect_remarks: e.target.value })}
                        className="mt-1 w-full px-2 py-1 text-[11px] border border-red-300 rounded bg-red-50 text-red-900"
                      />
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      type="button"
                      onClick={() => onOpenRowPreview(row)}
                      title="Live PDF preview"
                      className="p-1 rounded hover:bg-slate-100 text-blue-600 transition-colors cursor-pointer"
                    >
                      👁️
                    </button>
                  </td>
                  <td className="py-2 px-2 text-center">
                    {isReady ? (
                      <span title="Ready to renew" className="text-emerald-600 font-bold text-sm">✓</span>
                    ) : (
                      <span title="Missing Competent Person" className="text-amber-500 font-bold text-sm">⚠️</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-center">
                    <button
                      type="button"
                      onClick={() => onRemoveRow(row.id)}
                      title="Remove from bulk batch"
                      className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors cursor-pointer"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
